const storageKey = "billiard.workforce-phone-change-activation.v1";
const tokenPattern = /^.{32,512}$/u;
const invitationIdPattern = /^invite_[a-f0-9]{32}$/u;
const challengeIdPattern = /^new_phone_otp_[a-f0-9]{32}$/u;
const phonePattern = /^[0-9+\s()（）._:/／-]{6,40}$/u;

export interface WorkforcePhoneChangeActivationRecovery {
  version: 1;
  activationToken: string;
  invitationId: string;
  newPhone: string | null;
  maskedNewPhone: string | null;
  newPhoneOtpChallengeId: string | null;
  simulatedNewPhoneOtpCode: string | null;
  requestIdempotencyKey: string;
  verifyIdempotencyKey: string;
}

function defaultKeyFactory(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function nullableString(
  value: unknown,
  predicate: (candidate: string) => boolean,
): value is string | null {
  return value === null || (typeof value === "string" && predicate(value));
}

function validKey(value: unknown): value is string {
  return typeof value === "string" && value.length >= 8 && value.length <= 128;
}

export function parseWorkforcePhoneChangeActivationRecovery(
  raw: string | null,
): WorkforcePhoneChangeActivationRecovery | null {
  if (raw === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const expectedKeys = [
    "activationToken",
    "invitationId",
    "maskedNewPhone",
    "newPhone",
    "newPhoneOtpChallengeId",
    "requestIdempotencyKey",
    "simulatedNewPhoneOtpCode",
    "verifyIdempotencyKey",
    "version",
  ].sort();
  const actualKeys = Object.keys(record).sort();
  if (
    actualKeys.length !== expectedKeys.length ||
    actualKeys.some((key, index) => key !== expectedKeys[index]) ||
    record.version !== 1 ||
    typeof record.activationToken !== "string" ||
    !tokenPattern.test(record.activationToken) ||
    typeof record.invitationId !== "string" ||
    !invitationIdPattern.test(record.invitationId) ||
    !nullableString(record.newPhone, (candidate) => phonePattern.test(candidate)) ||
    !nullableString(
      record.maskedNewPhone,
      (candidate) => candidate.length >= 5 && candidate.length <= 40,
    ) ||
    !nullableString(record.newPhoneOtpChallengeId, (candidate) =>
      challengeIdPattern.test(candidate),
    ) ||
    !nullableString(record.simulatedNewPhoneOtpCode, (candidate) => /^\d{6}$/u.test(candidate)) ||
    !validKey(record.requestIdempotencyKey) ||
    !validKey(record.verifyIdempotencyKey) ||
    (record.newPhoneOtpChallengeId !== null && record.newPhone === null) ||
    (record.newPhoneOtpChallengeId !== null && record.maskedNewPhone === null) ||
    (record.maskedNewPhone !== null && record.newPhoneOtpChallengeId === null) ||
    (record.simulatedNewPhoneOtpCode !== null && record.newPhoneOtpChallengeId === null)
  ) {
    return null;
  }
  return Object.freeze({
    version: 1,
    activationToken: record.activationToken,
    invitationId: record.invitationId,
    newPhone: record.newPhone,
    maskedNewPhone: record.maskedNewPhone,
    newPhoneOtpChallengeId: record.newPhoneOtpChallengeId,
    simulatedNewPhoneOtpCode: record.simulatedNewPhoneOtpCode,
    requestIdempotencyKey: record.requestIdempotencyKey,
    verifyIdempotencyKey: record.verifyIdempotencyKey,
  });
}

export function createWorkforcePhoneChangeActivationRecovery(
  activationToken: string,
  invitationId: string,
  keyFactory: (prefix: string) => string = defaultKeyFactory,
): WorkforcePhoneChangeActivationRecovery {
  const recovery: WorkforcePhoneChangeActivationRecovery = {
    version: 1,
    activationToken,
    invitationId,
    newPhone: null,
    maskedNewPhone: null,
    newPhoneOtpChallengeId: null,
    simulatedNewPhoneOtpCode: null,
    requestIdempotencyKey: keyFactory("workforce-phone-change-new-phone-request"),
    verifyIdempotencyKey: keyFactory("workforce-phone-change-new-phone-verify"),
  };
  const parsed = parseWorkforcePhoneChangeActivationRecovery(JSON.stringify(recovery));
  if (!parsed) throw new Error("手机号更换进度内容不完整");
  return parsed;
}

export function saveWorkforcePhoneChangeActivationRecovery(
  storage: Storage,
  recovery: WorkforcePhoneChangeActivationRecovery,
): WorkforcePhoneChangeActivationRecovery {
  const protectedState = parseWorkforcePhoneChangeActivationRecovery(JSON.stringify(recovery));
  if (!protectedState) throw new Error("手机号更换进度内容无法安全保存");
  const serialized = JSON.stringify(protectedState);
  storage.setItem(storageKey, serialized);
  const confirmed = parseWorkforcePhoneChangeActivationRecovery(storage.getItem(storageKey));
  if (!confirmed || JSON.stringify(confirmed) !== serialized) {
    throw new Error("手机号更换进度无法安全保存，已停止继续提交");
  }
  return confirmed;
}

export function loadOrCreateWorkforcePhoneChangeActivationRecovery(
  storage: Storage,
  activationToken: string,
  invitationId: string,
): WorkforcePhoneChangeActivationRecovery {
  const raw = storage.getItem(storageKey);
  const existing = parseWorkforcePhoneChangeActivationRecovery(raw);
  if (raw !== null && existing === null) {
    throw new Error("本标签页保存的手机号更换进度已经损坏，已停止继续提交");
  }
  if (existing) {
    if (existing.activationToken !== activationToken || existing.invitationId !== invitationId) {
      throw new Error("本标签页保存的是另一笔手机号更换，已停止继续提交");
    }
    return existing;
  }
  return saveWorkforcePhoneChangeActivationRecovery(
    storage,
    createWorkforcePhoneChangeActivationRecovery(activationToken, invitationId),
  );
}

export function ensureWorkforcePhoneChangeActivationRecovery(
  storage: Storage,
  expected: WorkforcePhoneChangeActivationRecovery,
): WorkforcePhoneChangeActivationRecovery {
  const current = parseWorkforcePhoneChangeActivationRecovery(storage.getItem(storageKey));
  if (!current || JSON.stringify(current) !== JSON.stringify(expected)) {
    throw new Error("当前标签页保存的手机号更换进度已经变化，已停止继续提交");
  }
  return current;
}

export function clearWorkforcePhoneChangeActivationRecovery(storage: Storage): void {
  storage.removeItem(storageKey);
  if (storage.getItem(storageKey) !== null) {
    throw new Error("手机号更换安全进度无法清除，请关闭当前标签页");
  }
}

export function resetWorkforcePhoneChangeActivationRecoveryForTests(): void {
  if (!import.meta.env.DEV) throw new Error("test-only phone change activation recovery reset");
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // A storage-unavailable test intentionally makes this best-effort cleanup fail.
  }
}
